import { EligibilityResultStatus } from "../utils/eligibilityResult";

/**
 * Client-approved final-report copy for the three eligibility tiers.
 *
 * These are the headings the applicant sees. The per-answer statuses used
 * while scoring ("Eligible to File", "Potentially Eligible – Review Required",
 * "Not Currently Eligible") are internal and must never appear as a heading.
 *
 * Every acknowledgement is required before the applicant can continue. Yellow
 * and red keep the attorney recommendation but are not blocked from preparing
 * their forms.
 */
export type EligibilityResultContent = {
  heading: string;
  /** Automated-assessment paragraph. */
  assessment: string;
  /** Legal-disclosure paragraph shown under the assessment. */
  disclosure: string;
  acknowledgements: string[];
};

/**
 * Version of the acknowledgement wording below (client worklist #79).
 *
 * The server records which version an applicant agreed to, and refuses any
 * other. It must equal CURRENT_VERSION in
 * Portal-Backend/config/eligibilityAcknowledgement.js — bump both whenever the
 * client-approved wording changes, so an agreement to the old text is not
 * mistaken for agreement to the new.
 */
export const ELIGIBILITY_ACKNOWLEDGEMENT_VERSION = "2026-09-29.1";

export const CONSENT_SUFFIX =
  "I acknowledge that I have read and understood this eligibility assessment and the above disclosures, and I agree to the Terms of Service and Privacy Policy.";

export const ELIGIBILITY_RESULT_CONTENT: Record<
  EligibilityResultStatus,
  EligibilityResultContent
> = {
  pass: {
    heading: "Likely Eligible to Apply",
    assessment:
      "Based on the information you provided, you likely meet the automated eligibility criteria for the selected naturalization category.",
    disclosure:
      "This assessment is generated automatically from your responses and does not constitute legal advice, a legal opinion, or a determination of eligibility. USCIS is solely responsible for determining whether you are eligible for naturalization and whether your application will be approved.",
    acknowledgements: [
      "I understand that this eligibility assessment is based solely on the information I provided and has not been independently verified by Let's Get Visa.",
      "I understand that Let's Get Visa does not guarantee my eligibility for naturalization or the approval of my Form N-400.",
      "I understand that USCIS has sole authority to determine my eligibility and make all decisions regarding my application.",
      "I acknowledge that I am solely responsible for the accuracy, completeness, and truthfulness of all information I submit through this platform.",
      "I understand that by creating an account and continuing, I am using Let's Get Visa as a self-service application preparation platform and not as a substitute for legal advice.",
    ],
  },
  warn: {
    heading: "Additional Review Recommended",
    assessment:
      "Based on your responses, our automated assessment identified one or more issues that may require additional legal or factual review before your eligibility can be confirmed.",
    disclosure:
      "You may continue preparing your application; however, Let's Get Visa strongly recommends consulting a qualified immigration attorney before filing your Form N-400.",
    acknowledgements: [
      "I understand that this assessment is an automated screening tool and does not determine my legal eligibility for naturalization.",
      "I understand that my responses indicate one or more issues that may affect my application and that additional legal review is recommended.",
      "I understand that proceeding without obtaining legal advice may result in delays, requests for evidence, denial of my application, or other immigration consequences.",
      "I understand that USCIS has sole authority to determine my eligibility and decide my application.",
      "I acknowledge that I am choosing to continue preparing my application despite the issues identified by the automated assessment.",
      "I understand that Let's Get Visa is not responsible for decisions made by USCIS based on my application or information I provide.",
    ],
  },
  fail: {
    heading: "Eligibility Concerns Identified",
    assessment:
      "Based on the information you provided, our automated assessment identified one or more eligibility concerns for the selected naturalization category.",
    disclosure:
      "You may continue using the platform to prepare your application; however, filing before resolving these concerns may significantly affect the outcome of your application. Let's Get Visa strongly recommends consulting a qualified immigration attorney before filing.",
    acknowledgements: [
      "I understand that the automated assessment identified one or more concerns that may affect my eligibility for naturalization.",
      "I understand that this assessment is based solely on the information I provided and is not legal advice or a legal determination of eligibility.",
      "I understand that proceeding with my application despite these identified concerns may result in delays, requests for evidence, denial of my application, or other immigration consequences.",
      "I understand that USCIS has sole authority to determine my eligibility and decide my application.",
      "I acknowledge that I have been advised to seek legal counsel before filing and that I have chosen to continue preparing my application.",
      "I acknowledge that I am solely responsible for my decision to proceed and for the accuracy and completeness of the information I provide.",
    ],
  },
};

/** The consent sentence shown directly above the CTA. */
export const buildConsentSentence = (ctaLabel: string): string =>
  `By selecting "${ctaLabel}," ${CONSENT_SUFFIX}`;
